import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { roundBloomKernel } from '../vfx/bloomKernel';

export interface PostFxDeps {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
}

export interface PostFxHandle {
  /** Draw the scene through the bloom chain from the camera the world is flying. */
  render(activeCamera: THREE.Camera): void;
  dispose(): void;
}

export function createPostFx(deps: PostFxDeps): PostFxHandle {
  const { renderer, scene, camera } = deps;
  // ---------- Post-processing ----------
  const composer = new EffectComposer(renderer);
  const renderPass = new RenderPass(scene, camera);
  composer.addPass(renderPass);
  const bloom = new UnrealBloomPass(
    new THREE.Vector2(window.innerWidth, window.innerHeight),
    0.75,
    0.6,
    0.72,
  );
  // three's stock blur is cut at one sigma and draws a bright flash's halo as a box.
  roundBloomKernel(bloom);
  composer.addPass(bloom);
  composer.addPass(new OutputPass());

  // ---------- Resize ----------
  function onResize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    renderer.setSize(w, h);
    composer.setSize(w, h);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    bloom.resolution.set(w, h);
  }
  window.addEventListener('resize', onResize);

  return {
    render: (activeCamera) => {
      renderPass.camera = activeCamera;
      composer.render();
    },
    dispose: () => {
      window.removeEventListener('resize', onResize);
      composer.dispose();
    },
  };
}
